
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { CivilDate } from 'tc39-proposal-temporal';
import { WithArrowKeys } from '@bingads-webui-react/hoc-with-arrow-keys';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { keyCode } from '@bingads-webui/keycode';

import { generateKey } from './calendar-month';
import { YearItem } from './year-item';

class CalendarYearInternal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      yearStart: props.year,
      yearEnd: props.year + 11,
      focusedYear: props.year,
    };
    const {
      observable,
    } = props;

    let targetDate = null;
    const observer = {
      next: (data) => {
        switch (data) {
          case keyCode.ArrowUp:
            targetDate = this.addYear(this.state.focusedYear, -4);
            this.setState({
              focusedYear: targetDate,
            });
            break;
          case keyCode.ArrowDown:
            targetDate = this.addYear(this.state.focusedYear, 4);
            this.setState({
              focusedYear: targetDate,
            });
            break;
          case keyCode.ArrowLeft:
            targetDate = this.addYear(this.state.focusedYear, -1);
            this.setState({
              focusedYear: targetDate,
            });
            break;
          case keyCode.ArrowRight:
            targetDate = this.addYear(this.state.focusedYear, 1);
            this.setState({
              focusedYear: targetDate,
            });
            break;
          default:
            break;
        }
      },
    };
    observable.subscribe(observer);
  }

  getRange = () => {
    const {
      yearStart,
      yearEnd,
    } = this.state;

    const years = _.range(yearStart, yearEnd + 1);
    const result = [];
    const { length } = years;
    let i = 0;
    while (i < length) {
      const tempArr = years.slice(i, i + 4);
      result.push(tempArr);
      i += 4;
    }
    return result;
  }

  addYear = (date, year) => {
    if (year + date > this.state.yearEnd || year + date < this.state.yearStart) {
      return this.state.focusedYear;
    }
    return date + year;
  }

  isYearInRange = (year) => {
    const { maxDate, minDate } = this.props;
    if (minDate && year < minDate.year) return false;
    if (maxDate && year > maxDate.year) return false;
    return true;
  }

  canNavigateToPrevYearRange = () => {
    const { minDate } = this.props;
    return !minDate || this.state.yearStart > minDate.year;
  }

  canNavigateToNextYearRange = () => {
    const { maxDate } = this.props;
    return !maxDate || this.state.yearEnd < maxDate.year;
  }

  navigateToPrevYearRange = () => {
    if (this.canNavigateToPrevYearRange()) {
      this.setState(prevState => ({
        yearEnd: prevState.yearStart - 1,
        yearStart: prevState.yearStart - 12,
      }));
    }
  }

  navigateToNextYearRange = () => {
    if (this.canNavigateToNextYearRange()) {
      this.setState(prevState => ({
        yearEnd: prevState.yearEnd + 12,
        yearStart: prevState.yearEnd + 1,
      }));
    }
  }

  focusItemCallback = (year) => {
    this.setState({
      focusedYear: year,
    });
  }

  render() {
    const {
      yearStart,
      yearEnd,
    } = this.state;

    const {
      onSelectYear,
    } = this.props;

    const years = this.getRange();
    return (
      <div className="calendar-year">
        <div className="header">
          <div className="year-range">
            <div className="navContainer">
              <div
                className="prevMonth"
                tabIndex="0"
                role="button"
                onClick={this.navigateToPrevYearRange}
                onKeyDown={this.canNavigateToPrevYearRange() ?
                  event => submitEvent(this.navigateToPrevYearRange)({ keyCode: event.which })
                  : undefined}
              >
                <span className={classNames([this.canNavigateToPrevYearRange() ? '' : 'disabled', 'glyphicon', 'glyphicon-menu-left'])} />
              </div>
              <div className="monthAndYear monthYearButton">
                <span> {yearStart} - {yearEnd} </span>
              </div>
              <div
                className="nextMonth"
                tabIndex="0"
                role="button"
                onClick={this.navigateToNextYearRange}
                onKeyDown={this.canNavigateToNextYearRange() ?
                  event => submitEvent(this.navigateToNextYearRange)({ keyCode: event.which })
                  : undefined}
              >
                <span className={classNames([this.canNavigateToNextYearRange() ? '' : 'disabled', 'glyphicon', 'glyphicon-menu-right'])} />
              </div>
            </div>
          </div>

          <div>
            <table>
              <tbody>
                {
                  _.map(years, (yearRange, index) => (
                    <tr
                      className="row"
                      key={index}
                    >
                      {
                        _.map(yearRange, (year, yearIndex) => {
                          const idx = index;
                          return (
                            <YearItem
                              key={generateKey(idx, yearIndex)}
                              yearIdx={generateKey(idx, yearIndex)}
                              year={year}
                              isYearInRange={this.isYearInRange}
                              onSelectYear={onSelectYear}
                              focusItemCallback={this.focusItemCallback}
                              isFocused={
                                year === this.state.focusedYear
                              }
                            />
                          );
                        })
                      }

                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}

CalendarYearInternal.propTypes = {
  year: PropTypes.number.isRequired,
  onSelectYear: PropTypes.func.isRequired,
  observable: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
  }).isRequired,
  minDate: PropTypes.instanceOf(CivilDate).isRequired,
  maxDate: PropTypes.instanceOf(CivilDate).isRequired,
};

export const CalendarYear = WithArrowKeys(CalendarYearInternal);
